import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { CardStatesEnum, CardTypesEnum, CustomerCardDetailVm, CustomerCardTypesEnum, CustomerDetailVm } from '@/api';
import { AccountTypes } from '@/constants/AccountTypes';
import { MIN_CUSTOMER_AGE } from '@/config/config';
import { toDate } from '@/utils/dateTime';
import { differenceInYears, startOfDay } from 'date-fns';
import { supportsEWallet } from '@t-system/common/src/utils/cardType';
import { useSystemConfigurationStore } from '@/store/useSystemConfigurationStore.ts';

export const useUserAccountStore = defineStore('userAccount', () => {
  const systemConfigurationStore = useSystemConfigurationStore();

  const hasActiveOrProcessedCardApplication = ref(false);
  const cardDetailData = ref<CustomerCardDetailVm>({
    state: {},
    customer: {},
    electronicWallet: {},
    cardProfiles: [],
  } as unknown as CustomerCardDetailVm);
  const selectedCustomerData = ref<CustomerDetailVm>({} as CustomerDetailVm);
  const selectedAccount = ref(AccountTypes.REGISTERED);
  const canVirtualCardApplicationBeCreated = ref(true);
  const cardLoaded = ref(false);

  const isCardActive = computed(() => {
    return cardDetailData.value?.state.name === CardStatesEnum.ACTIVE;
  });

  const isCardTypeMobilePhone = computed(() => {
    return cardDetailData.value?.type?.name === CardTypesEnum.MOBILE_PHONE;
  });

  const isCardTypeBank = computed(() => {
    return cardDetailData.value?.type?.name === CardTypesEnum.BANK_CARD;
  });

  const isCardTypeWithoutStructure = computed(() => {
    return cardDetailData.value?.type?.name === CardTypesEnum.SMART_CARD_WITHOUT_STRUCTURE;
  });

  const isCardTypeWithStructure = computed(() => {
    return (
      cardDetailData.value?.type?.name === CardTypesEnum.SMART_CARD ||
      cardDetailData.value?.type?.name === CardTypesEnum.SMART_CARD_2
    );
  });

  const isCardTypeWithChip = computed(() => {
    return cardDetailData.value?.customerCardType === CustomerCardTypesEnum.PERSONAL;
  });

  const isCardTypePortable = computed(() => {
    return cardDetailData.value?.customerCardType === CustomerCardTypesEnum.PORTABLE;
  });

  const isCardTypeVirtual = computed(() => {
    return cardDetailData.value?.customerCardType === CustomerCardTypesEnum.VIRTUAL_CARD;
  });

  const isCustomerProfileApplicationAllowed = computed(() => {
    const type = cardDetailData.value?.type?.name;
    return !!(
      systemConfigurationStore.isCustomerProfileApplicationAllowed &&
      isCardActive.value &&
      type &&
      ([CardTypesEnum.MOBILE_PHONE, CardTypesEnum.WHITE_LIST_CARD, CardTypesEnum.EXTERNAL_CARD] as string[]).includes(
        type,
      )
    );
  });

  const isCardExpired = computed(() => {
    return cardDetailData.value?.state.name === CardStatesEnum.EXPIRED;
  });

  const cardNumber = computed(() => {
    return cardDetailData.value?.cardNumber;
  });

  const hasCardDetailData = computed(() => {
    return !!cardDetailData.value?.id;
  });

  const personalDataVerified = computed(() => {
    return Boolean(cardDetailData.value?.customer?.personalDataVerifiedDate);
  });

  const isSelectedAccountRegistered = computed(() => {
    return selectedAccount.value === AccountTypes.REGISTERED;
  });

  const customerIsChild = computed(() => {
    const currentDate = toDate(new Date());
    const birthDay = selectedCustomerData.value.birthDay;
    if (!birthDay) {
      return false;
    }

    const customerYears = differenceInYears(startOfDay(currentDate), startOfDay(birthDay));

    return customerYears < MIN_CUSTOMER_AGE;
  });

  const isCardActiveAndSupportsEWallet = (enabledEWalletOnVirtualCard: boolean) => {
    return (
      isCardActive.value &&
      supportsEWallet(cardDetailData.value?.type?.name as CardTypesEnum, enabledEWalletOnVirtualCard)
    );
  };

  const setCardData = (payload: CustomerCardDetailVm) => {
    cardDetailData.value = payload;
  };

  const setSelectedCustomerData = (payload: CustomerDetailVm) => {
    selectedCustomerData.value = payload;
  };

  const setCardApplicationActiveOrProcessed = (activeOrProcessed: boolean) => {
    hasActiveOrProcessedCardApplication.value = activeOrProcessed;
  };

  const setSelectedAccount = (accountType: string) => {
    selectedAccount.value = accountType;
  };

  const clearSelectedCustomerData = () => {
    selectedCustomerData.value = {} as CustomerDetailVm;
  };

  const setVirtualCardApplicationCanBeCreated = (canBeCreated: boolean) => {
    canVirtualCardApplicationBeCreated.value = canBeCreated;
  };

  const setCardLoaded = () => {
    cardLoaded.value = true;
  };

  const clearCardData = () => {
    cardDetailData.value = {
      state: {},
      customer: {},
      electronicWallet: {},
    } as CustomerCardDetailVm;
  };

  return {
    hasActiveOrProcessedCardApplication,
    cardDetailData,
    selectedCustomerData,
    selectedAccount,
    canVirtualCardApplicationBeCreated,
    isCardActive,
    isCardTypeMobilePhone,
    isCardTypeBank,
    isCardTypeWithoutStructure,
    isCardTypeWithStructure,
    isCardTypeWithChip,
    isCardTypePortable,
    isCardTypeVirtual,
    isCustomerProfileApplicationAllowed,
    isCardExpired,
    cardNumber,
    hasCardDetailData,
    personalDataVerified,
    isSelectedAccountRegistered,
    customerIsChild,
    cardLoaded,

    isCardActiveAndSupportsEWallet,
    setCardData,
    setSelectedCustomerData,
    setCardApplicationActiveOrProcessed,
    setSelectedAccount,
    clearSelectedCustomerData,
    setVirtualCardApplicationCanBeCreated,
    setCardLoaded,
    clearCardData,
  };
});
